import React, { useEffect, useState } from "react";
import { Box, Alert, Typography, Snackbar, Alert as MuiAlert } from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { ActionButton } from "../../components/atoms";
import { useClinicTheme } from "../../theme";
import { useHasPermission } from "../../hooks/useHasPermission";
import useStaffManagement from "./useStaffManagement";
import StaffManagementHeader from "./StaffManagementHeader";
import StaffTable from "./StaffTable";
import UserActionsMenu from "../UserManagement/UserActionsMenu";
import UserDetailDrawer from "../UserManagement/UserDetailDrawer";
import UserActionDialog from "../UserManagement/UserActionDialog";
import UserDeleteDialog from "../UserManagement/UserDeleteDialog";
import { COLORS } from "../../styles/uiConstants";

export default function StaffManagement() {
  const { palette } = useClinicTheme();
  const vm = useStaffManagement();

  // Klinik personeli için hesap durumu yönetimi ve silme yetkileri
  const canManageStatus = useHasPermission("users:manageStatus");
  const canDelete = useHasPermission("users:delete");

  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 900
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const h = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  if (vm.usersError) {
    return (
      <Box sx={{ p: 3, background: palette.backgroundSoft, minHeight: "100%" }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, color: palette.primary, fontWeight: 700 }}>
          Personel Yönetimi
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Hata:</strong> Personel listesi yüklenirken bir hata oluştu
        </Alert>
        <ActionButton
          palette={palette}
          size="medium"
          label="Tekrar Dene"
          startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
          onClick={vm.refreshUsers}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${COLORS.slate50} 0%, ${COLORS.slate200} 100%)`,
        p: { xs: 2, sm: 3, md: 4 },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          height: "100%",
          minHeight: 0,
        }}
      >
        <StaffManagementHeader
          counts={vm.counts}
          statusFilter={vm.statusFilter}
          onStatusFilterChange={(v) => {
            vm.setStatusFilter(v);
            vm.setPage(0);
          }}
          searchName={vm.searchName}
          onSearchNameChange={(v) => {
            vm.setSearchName(v);
            vm.setPage(0);
          }}
          searchEmail={vm.searchEmail}
          onSearchEmailChange={(v) => {
            vm.setSearchEmail(v);
            vm.setPage(0);
          }}
        />

        <StaffTable
          pagedUsers={vm.pagedUsers}
          filteredCount={vm.filteredUsers.length}
          isLoading={vm.isLoadingUsers}
          page={vm.page}
          rowsPerPage={vm.rowsPerPage}
          onPageChange={(e, p) => vm.setPage(p)}
          onRowsPerPageChange={(e) => {
            vm.setRowsPerPage(parseInt(e.target.value, 10));
            vm.setPage(0);
          }}
          onMenuOpen={vm.openMenu}
          viewportHeight={viewportHeight}
        />
      </Box>

      <UserActionsMenu
        anchorEl={vm.menuAnchorEl}
        user={vm.menuUser}
        onClose={vm.closeMenu}
        onDetail={vm.openDetail}
        onAction={vm.openActionDialog}
        onDelete={vm.openDeleteDialog}
        canUpdate={canManageStatus}
        canDelete={canDelete}
      />

      <UserDetailDrawer
        open={vm.detailDrawerOpen}
        onClose={() => vm.setDetailDrawerOpen(false)}
        user={vm.selectedUser}
      />

      <UserActionDialog
        open={vm.dialogOpen}
        dialogType={vm.dialogType}
        selectedUser={vm.selectedUser}
        reason={vm.reason}
        onReasonChange={vm.setReason}
        endDate={vm.endDate}
        onEndDateChange={vm.setEndDate}
        onClose={vm.closeActionDialog}
        onConfirm={vm.handleAccountAction}
        isLoading={vm.isActionLoading}
      />

      <UserDeleteDialog
        open={vm.deleteDialogOpen}
        user={vm.deleteTargetUser}
        onClose={() => {
          vm.setDeleteDialogOpen(false);
          vm.setDeleteTargetUser(null);
        }}
        onConfirm={vm.handleDeleteConfirm}
        isDeleting={vm.isDeleting}
      />

      <Snackbar
        open={vm.snackbar.open}
        autoHideDuration={6000}
        onClose={vm.closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={vm.closeSnackbar}
          severity={vm.snackbar.severity}
          sx={{ width: "100%" }}
        >
          {vm.snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
